import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/styles';

import arrowLeftImg from 'assets/images/left-arrow.png';
import arrowRightImg from 'assets/images/right-arrow.png';
import useInterval from 'utils/useInterval';

import SliderControls from './SliderControls';

const styles = theme => ({
  root: {
    width: '100%',
    position: 'relative',
  },
  slide: {
    opacity: 0,
    display: 'block',
    transition: 'opacity 300ms ease-in',
    willChange: 'opacity',
  },
  currentSlide: {
    opacity: 1,
    transition: 'opacity 300ms ease-out',
  },
  fixedSlide: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  controls: {
    width: '100%',
    padding: `${theme.spacing.s8}px 0`,
  },
  floatingControls: {
    width: '100%',
    position: 'absolute',
    bottom: 20,
    zIndex: 2,
  },

  next: {
    width: '50%',
    height: '100%',
    position: 'absolute',
    right: 0,
    top: 0,
    '&:hover': { cursor: `url(${arrowRightImg}), auto` },
  },
  prev: {
    top: 0,
    width: '50%',
    height: '100%',
    position: 'absolute',
    '&:hover': { cursor: `url(${arrowLeftImg}), auto` },
  },
});

const SliderVariant = ({
  autoPlay,
  classes,
  children,
  controlsTheme,
  dataFrom,
  floatingControls,
  hideControls,
  onIndexChange,
}) => {
  const size = children?.length || 0;
  const [index, setIndex] = useState(0);
  const [auto, setAuto] = useState(autoPlay);

  useEffect(() => {
    onIndexChange && onIndexChange(index);
  }, [onIndexChange, index]);

  const toggleAuto = () => autoPlay && setAuto(!auto);
  const increment = () => setIndex((index + 1) % size);
  const decrement = () => {
    setIndex((index || size) - 1);
  };

  useInterval(increment, auto ? 4000 : null);

  return (
    <div className={classes.root} onMouseEnter={toggleAuto} onMouseLeave={toggleAuto}>
      {children.map((child, i) => (
        <div
          key={i}
          className={classNames(classes.slide, {
            [classes.fixedSlide]: i > 0,
            [classes.currentSlide]: i === index,
          })}
          data-click="change-slide"
          data-from={dataFrom}
          data-testid="slide"
        >
          {child}
        </div>
      ))}
      <div className={classes.prev} onClick={decrement} />
      <div className={classes.next} onClick={increment} />
      {size > 1 && !hideControls && (
        <SliderControls
          classes={{
            root: floatingControls ? classes.floatingControls : classes.controls,
          }}
          currentIndex={index}
          dataFrom="block-consultation-slider"
          onClick={i => setIndex(i)}
          size={size}
          theme={controlsTheme}
        />
      )}
    </div>
  );
};

SliderVariant.defaultProps = {
  autoPlay: false,
  dataFrom: 'image-slider',
  floatingControls: false,
};

SliderVariant.propTypes = {
  autoPlay: PropTypes.bool,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  children: PropTypes.array.isRequired,
  controlsTheme: PropTypes.string,
  dataFrom: PropTypes.string,
  floatingControls: PropTypes.bool,
  hideControls: PropTypes.bool,
  onIndexChange: PropTypes.func,
};

export default withStyles(styles)(SliderVariant);
